/* ---------------------------------------------------------------------------
 * The server browser: search, context switch, filter rows, facets, the HeroUI
 * table and the pager. Moved verbatim from player/src/App.tsx so the site and
 * the launcher render the same component. Data access, analytics and the ping
 * estimate are injected (props) instead of imported, so the launcher can pass
 * its IPC-backed client and its real per-server ping. docs/STYLE.md is law.
 * ------------------------------------------------------------------------- */
import { createContext, memo, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Button, Card, Input, Label, Table, TextField } from '@heroui/react'
import {
  AlertTriangle, ChevronDown, ChevronLeft, ChevronRight, Gauge, Moon, Search,
  Sliders as SlidersHorizontal, StarFilled, Sun, Users, Clock as ClockIcon,
} from './Icons'
import { ChevronsLeft, ChevronsRight, X } from 'lucide-react'
import { RangePopover, Segmented, IconToggle } from './FilterControls'
import { Star, RankInfo, VanillaBar, chipClass, Tip, InfoGlyph, features } from './lib'
import { fmt, flag, mapLabel } from './format'
import type { FilterOptions, ServerBrowserApi, ServerFilters, ServerRow, TrackFn, UrlFilter } from './types'

/* Injected environment: the API client and the analytics hook. Provided by the
   ServerBrowser to its nested components (ModSearch) so nothing is threaded. */
const BrowserEnv = createContext<{ api: ServerBrowserApi; track: TrackFn }>({
  api: {
    servers: async () => ({ total: 0, total_all: 0, servers: [] }),
    filters: async () => ({ countries: [], maps: [], mods: [] }),
    modsSearch: async () => [],
    modInfo: async () => { throw new Error('no api') },
    countryInfo: async () => { throw new Error('no api') },
  },
  track: () => {},
})

// European Union member states (27) — keep in sync with EU_COUNTRIES in dashboard/app.py.
// EU-only on purpose: excludes RU, UA, GB, NO, CH, RS, BY, etc.
const EU_SET = new Set(['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'])

export function StarButton({ on, onToggle, size = 18 }: { on: boolean; onToggle: () => void; size?: number }) {
  return (
    <button type="button" aria-label={on ? 'Remove favourite' : 'Add favourite'} aria-pressed={on}
      onClick={(e) => { e.stopPropagation(); onToggle() }}
      className={`shrink-0 transition-colors ${on ? 'text-[var(--accent)]' : 'text-[var(--border-tertiary)] hover:text-[var(--muted)]'}`}>
      <Star size={size} fill={on ? 'currentColor' : 'none'} />
    </button>
  )
}

/* ------------------------------------------------------------------ Filter primitives */
export function FilterPill({ label, active, onChange, icon, tag }: { label: string; active: boolean; onChange: (v: boolean) => void; icon?: ReactNode; tag?: string }) {
  // Active = brand-orange ring + brand-orange text + subtle brand-tinted fill.
  // Rest = neutral surface, lightens (NOT darkens) on hover.
  // Height tuned to match icon-bearing pills (Players, In-game) so the row
  // reads as one consistent strip. `tag` is a small solid label after the
  // text ("New" on a filter for something that just arrived): brand fill
  // with the dark CTA ink, never white on orange, readable at a glance.
  return (
    <button type="button" onClick={() => onChange(!active)} aria-pressed={active}
      className={`mono text-xs uppercase tracking-wider rounded-full px-4 inline-flex items-center gap-1.5 transition-colors ${active
        ? 'bg-[var(--brand-soft)] text-[var(--brand)] font-semibold ring-1 ring-[var(--brand)] [--icon-accent:var(--brand)]'
        : 'bg-[var(--surface-1)] text-[var(--muted)] hover:text-[var(--foreground)] hover:bg-[var(--surface-1-hover)]'}`}
      style={{ minHeight: 40 }}>
      {icon && <span className="inline-flex shrink-0">{icon}</span>}
      <span>{label}</span>
      {tag && <span className="mono text-[10.5px] font-semibold uppercase tracking-wider rounded-[5px] px-1.5 py-[3px] leading-none bg-[var(--brand)] text-[#1B1B1B]">{tag}</span>}
    </button>
  )
}

function ChipToggleGroup({ label, options, selected, onToggle, max = 16 }: {
  label: string
  options: { id: string; label: string; count?: number }[]
  selected: string[]
  onToggle: (id: string) => void
  max?: number
}) {
  const [expanded, setExpanded] = useState(false)
  if (options.length === 0) return null
  const shown = expanded ? options : options.slice(0, max)
  return (
    <div>
      <div className="mono text-[11px] uppercase tracking-wider text-[var(--muted)] mb-2">
        {label}{selected.length > 0 && <span className="text-[var(--accent)]"> · {selected.length} selected</span>}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {shown.map((o) => {
          const on = selected.includes(o.id)
          return (
            <button key={o.id} type="button" onClick={() => onToggle(o.id)} aria-pressed={on}
              className={`px-2.5 py-1 ${chipClass(on)}`}>
              {o.label}{o.count != null && <span className="opacity-60"> {fmt(o.count)}</span>}
            </button>
          )
        })}
        {options.length > max && (
          <button type="button" onClick={() => setExpanded((e) => !e)}
            className="mono text-xs rounded-full px-2.5 py-1 bg-[var(--background-tertiary)] text-[var(--muted)] hover:text-[var(--foreground)]">
            {expanded ? 'show less' : `+${options.length - max} more`}
          </button>
        )}
      </div>
    </div>
  )
}

/* ----------------------------------------------- Custom mod filter chips */
// The popular-mods facet only carries the top ~80 mods, so users hunting servers
// by a niche mod search the full catalog (api.modsSearch) and pin the result as
// their own chip. Pinned chips persist in localStorage and carry a × to delete.
const CUSTOM_MODS_KEY = 'dzm_custom_mods'
function useCustomMods() {
  const [customMods, setCustomMods] = useState<{ id: string; name: string }[]>(() => {
    try { return JSON.parse(localStorage.getItem(CUSTOM_MODS_KEY) || '[]') } catch { return [] }
  })
  const save = (next: { id: string; name: string }[]) => {
    try { localStorage.setItem(CUSTOM_MODS_KEY, JSON.stringify(next)) } catch { /* quota */ }
    return next
  }
  const addCustomMod = (m: { id: string; name: string }) =>
    setCustomMods((xs) => (xs.some((x) => x.id === m.id) ? xs : save([...xs, m])))
  const removeCustomMod = (id: string) =>
    setCustomMods((xs) => save(xs.filter((x) => x.id !== id)))
  return { customMods, addCustomMod, removeCustomMod }
}

// Search box under the Mods chips: type ≥2 chars, pick a result to pin it as a
// chip. Results already present as chips are hidden from the list.
function ModSearch({ existing, onPick }: {
  existing: Set<string>
  onPick: (m: { id: string; name: string }) => void
}) {
  const { api, track: trackEvent } = useContext(BrowserEnv)
  const [q, setQ] = useState('')
  const [results, setResults] = useState<{ id: string; name: string; count: number }[] | null>(null)
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    const query = q.trim()
    if (query.length < 2) { setResults(null); setBusy(false); return }
    setBusy(true)
    let on = true
    const t = setTimeout(() => {
      api.modsSearch(query)
        .then((r) => {
          if (!on) return
          // Mods without a workshop id still filter fine server-side (name match),
          // so fall back to the name as the chip value; drop fully anonymous rows.
          setResults(r.flatMap((m) => {
            const id = m.id ?? m.name
            return id && m.name ? [{ id, name: m.name, count: m.count }] : []
          }))
        })
        .catch(() => { if (on) setResults([]) })
        .finally(() => { if (on) setBusy(false) })
    }, 300)
    return () => { on = false; clearTimeout(t) }
  }, [q])
  const shown = (results ?? []).filter((m) => !existing.has(m.id))
  return (
    <div>
      <div className="relative max-w-[340px]">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)] pointer-events-none" />
        <input
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search all mods…" aria-label="Search all mods"
          className="w-full mono text-xs rounded-full bg-[var(--background-tertiary)] text-[var(--foreground)] placeholder:text-[var(--muted)] pl-9 pr-9 py-2 outline-none focus:ring-1 focus:ring-[var(--brand)]" />
        {busy && <span className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full border-2 border-[var(--background-secondary)] border-t-[var(--brand)] animate-spin" />}
      </div>
      {results != null && !busy && (
        shown.length > 0 ? (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {shown.slice(0, 12).map((m) => (
              <button key={m.id} type="button"
                onClick={() => { trackEvent('filter_use', 'mod_pin', m.id); onPick({ id: m.id, name: m.name }) }}
                className={`px-2.5 py-1 ${chipClass(false)}`}>
                + {m.name.slice(0, 28)}<span className="opacity-60"> {fmt(m.count)}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="mono text-[11px] text-[var(--muted)] mt-2">
            {results.length > 0 ? 'Already added.' : 'No live servers run a mod by that name.'}
          </div>
        )
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ Server browser */
// Lead with our Rank (the whole point of the site). Unranked servers (no history
// yet) fall below the ranked leaders, busiest-first (tiebreak handled server-side).
// Defaults: online now, no password, and hide empty. What a player wants on
// first paint, and a smaller list to fetch and render.
export const defaultFilters: ServerFilters = { sort: 'rank', online: true, fakes: 'hide', hide_empty: true, open_only: true }

// Translate a URL-driven filter into a `ServerFilters` shape the browser can use.
// `rank` sort (ascending position) means "best first", which is what the URL
// implies ("show me servers running X" → users want the good ones first).
function seedFromUrlFilter(uf: UrlFilter): ServerFilters {
  // dir 'desc' = best first for the rank sort (rank_score, higher is better),
  // the same direction the homepage uses. 'asc' put the worst-ranked servers
  // at the top of every /map, /mod and /servers view under a "ranked best
  // first" banner.
  // scope must be explicit: without it the /map and /servers views mixed
  // Bohemia official servers into a list labelled "Community".
  const base: ServerFilters = { ...defaultFilters, scope: 'community', sort: 'rank', dir: 'desc' }
  if (uf.kind === 'mod') return { ...base, mod: [String(uf.id)] }
  if (uf.kind === 'map') return { ...base, map: [uf.map] }
  // For country we don't know the ISO code synchronously; the effect will fill
  // it in once /api/country-info resolves. Leave country empty so the table
  // briefly shows all servers (instead of an empty state with no signal).
  return base
}

// First-paint label for the context banner. The mod/country variants get
// improved asynchronously once /api/mod-info or /api/country-info resolves.
function urlContextFromFilter(uf: UrlFilter): { title: string; sub?: string } {
  if (uf.kind === 'mod')     return { title: `Servers running this mod`, sub: 'Loading…' }
  if (uf.kind === 'map')     return { title: `Servers on ${mapLabel(uf.map)}`, sub: 'Ranked best first' }
  return { title: `Servers in this country`, sub: 'Loading…' }
}
const PAGE_SIZE_DEFAULT = 30
const PAGE_SIZE_OPTIONS = [10, 25, 30, 50, 100] as const
const PAGE_SIZE_KEY = 'dzm_page_size'
const readPageSize = (): number => {
  try {
    const n = parseInt(localStorage.getItem(PAGE_SIZE_KEY) || '', 10)
    return PAGE_SIZE_OPTIONS.includes(n as typeof PAGE_SIZE_OPTIONS[number]) ? n : PAGE_SIZE_DEFAULT
  } catch { return PAGE_SIZE_DEFAULT }
}

// Recent searches — saved to localStorage on debounce-commit so the user can
// rerun a previous query in one click. Stored newest-first, deduped case-
// insensitively, capped at 8 entries.
const RECENT_KEY = 'dzm_recent_searches'
const RECENT_MAX = 8
const readRecents = (): string[] => {
  try { const v = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); return Array.isArray(v) ? v.filter((x) => typeof x === 'string').slice(0, RECENT_MAX) : [] }
  catch { return [] }
}
const writeRecents = (xs: string[]): void => {
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(xs.slice(0, RECENT_MAX))) } catch { /* quota */ }
}
const pushRecent = (q: string, cur: string[]): string[] => {
  const clean = q.trim()
  if (clean.length < 2) return cur
  const next = [clean, ...cur.filter((x) => x.toLowerCase() !== clean.toLowerCase())].slice(0, RECENT_MAX)
  writeRecents(next); return next
}

/* The server list itself: rows exactly as the browser draws them. The browser
   wraps it with search, filters, sort state and the pager; the launcher also
   renders it on its own (an open collection). */
export interface ServerTableProps {
  rows: ServerRow[]
  favSet: Set<number>; onToggleFav: (id: number) => void
  onOpen: (id: number) => void
  pingFor: (s: ServerRow) => number | null
  pingHeader: string; pingTitle: string; pingPrefix: string
  liveFor?: ServerBrowserProps['liveFor']
  rowAction?: ServerBrowserProps['rowAction']
  rowStart?: ServerBrowserProps['rowStart']
  // Launcher only: replaces the star cell (the launcher's star opens its
  // folder menu). The site keeps the plain toggle.
  starControl?: ServerBrowserProps['starControl']
  // Launcher only: the server whose join sheet is open gets a highlighted row.
  selectedId?: number | null
  sortDescriptor?: { column: string; direction: 'ascending' | 'descending' }
  onSortChange?: (d: { column?: string | number; direction?: string }) => void
  emptyState: ReactNode
  ariaLabel?: string
}

export function ServerTable({
  rows, favSet, onToggleFav, onOpen, pingFor, pingHeader, pingTitle, pingPrefix, liveFor, rowAction, rowStart, starControl, selectedId = null,
  sortDescriptor, onSortChange, emptyState, ariaLabel = 'Servers',
}: ServerTableProps) {
  return (
    <Table>
      <Table.ScrollContainer>
        <Table.Content aria-label={ariaLabel} selectionMode="none" sortDescriptor={sortDescriptor} onSortChange={onSortChange}>
          <Table.Header>
            <Table.Column id="fav" aria-label="Favourite"><Star size={16} /></Table.Column>
            {rowStart && <Table.Column id="start" aria-label="Status" className="w-8 !px-1" />}
            <Table.Column isRowHeader id="name" allowsSorting>Server</Table.Column>
            <Table.Column id="country" allowsSorting>Loc</Table.Column>
            <Table.Column id="map" allowsSorting>Map</Table.Column>
            <Table.Column id="players" allowsSorting className="text-right">Players</Table.Column>
            <Table.Column id="rank" allowsSorting className="text-right">Rank</Table.Column>
            <Table.Column id="ping" className="text-right whitespace-nowrap">{pingHeader}</Table.Column>
            <Table.Column id="mods" allowsSorting className="text-right">Mods</Table.Column>
            <Table.Column id="vanilla_scale" allowsSorting className="text-right">Mod level</Table.Column>
            {rowAction && <Table.Column id="action" aria-label="Action" className="text-right" />}
          </Table.Header>
          {/* dependencies: react-aria re-renders cached rows only when these
              change; the ping/live overlays are closures over changing state. */}
          <Table.Body items={rows} dependencies={[favSet, pingFor, liveFor, rowAction, rowStart, starControl, selectedId]} renderEmptyState={() => emptyState}>
            {(s: ServerRow) => {
              const isOn = s.status === 'online'
              return (
                <Table.Row key={s.id} onAction={() => onOpen(s.id)} className={selectedId === s.id ? 'cursor-pointer row-selected bg-[var(--surface-1-hover)]' : 'cursor-pointer'}>
                  <Table.Cell>{starControl ? starControl(s) : <StarButton on={favSet.has(s.id)} onToggle={() => onToggleFav(s.id)} size={14} />}</Table.Cell>
                  {/* Launcher only: a narrow status column (play / download glyph). */}
                  {rowStart && <Table.Cell className="!px-1">{rowStart(s)}</Table.Cell>}
                  <Table.Cell className="max-w-[var(--sb-name-max,360px)]">
                    <span className="flex items-center gap-1.5 min-w-0">
                      {s.featured && <span className="shrink-0 mono text-[8px] uppercase tracking-widest text-[var(--accent-foreground)] bg-[var(--accent)] rounded px-1 py-0.5">★ Feat</span>}
                      {/* wipe engine phase: translucent, never an orange fill; brand text marks the weekend before a wipe */}
                      {features.wipes && s.wipe_phase && <span className={`shrink-0 mono text-[8px] uppercase tracking-widest rounded px-1 py-0.5 bg-[rgba(255,255,255,0.08)] ${s.wipe_phase === 'wipe_weekend' ? 'text-[var(--brand)]' : 'text-[var(--foreground)]'}`}>{s.wipe_phase === 'fresh' ? 'Fresh wipe' : s.wipe_phase === 'wipe_weekend' ? 'Wipe weekend' : 'Wipe week'}</span>}
                      {s.mimics_official && (
                        <Tip tappable width={272} content={
                          <span>This server uses <b>Bohemia's official server naming</b> but is <b>not</b> an official server — it runs mods, sits on a private hive, or is on a non-official map. Real officials appear under the <b>Official</b> tab.</span>
                        }><span onClick={(e) => e.stopPropagation()} className="inline-flex shrink-0 text-[var(--brand)]">
                          <AlertTriangle size={16} noFloor />
                        </span></Tip>
                      )}
                      <span className="truncate">{s.name}</span>
                    </span>
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">{flag(s.country)} <span className="text-[var(--muted)]">{s.country}</span></Table.Cell>
                  <Table.Cell className="text-[var(--muted)]">{mapLabel(s.map)}</Table.Cell>
                  <Table.Cell className="mono text-right whitespace-nowrap">{isOn ? (() => {
                    const lv = liveFor?.(s)
                    const players = lv?.players ?? s.players
                    const maxp = lv?.maxPlayers ?? s.max_players
                    return <><span title={lv ? 'live, measured just now' : undefined}>{fmt(players)}/{fmt(maxp)}</span>{s.queue ? <span className="text-[var(--accent)]" title="players queued"> +{s.queue}</span> : null}</>
                  })() : <span className="text-[var(--muted)]">offline</span>}</Table.Cell>
                  <Table.Cell className="mono text-right text-[var(--accent)] whitespace-nowrap">{s.rank_pos != null ? `#${fmt(s.rank_pos)}` : '—'}</Table.Cell>
                  <Table.Cell className="mono text-right whitespace-nowrap"><span title={pingTitle}>{(() => { const e = pingFor(s); return e != null ? pingPrefix + e + 'ms' : '—' })()}</span></Table.Cell>
                  <Table.Cell className="mono text-right">{s.mod_count ?? '—'}</Table.Cell>
                  <Table.Cell className="text-right"><span className="inline-flex justify-end w-full"><VanillaBar score={s.vanilla_score} band={s.vanilla_band} /></span></Table.Cell>
                  {rowAction && <Table.Cell className="text-right">{rowAction(s)}</Table.Cell>}
                </Table.Row>
              )
            }}
          </Table.Body>
        </Table.Content>
      </Table.ScrollContainer>
    </Table>
  )
}

export interface ServerBrowserProps {
  opts: FilterOptions | null; onOpen: (id: number, highlight?: string[]) => void
  favs: number[]; onToggleFav: (id: number) => void
  // Data access and analytics are injected so the same component serves the
  // site (fetch + first-party beacon) and the launcher (IPC + opt-in telemetry).
  api: ServerBrowserApi
  track?: TrackFn
  // The ping column. The site estimates from distance (a browser cannot measure
  // game-server ping); the launcher measures it from the player's machine.
  pingFor: (s: ServerRow) => number | null
  pingHeader?: string
  pingTitle?: string
  pingPrefix?: string
  // Launcher only: live players/queue measured from the player's machine
  // overlay the API's numbers in the Players cell (plan §5 merge rule).
  liveFor?: (s: ServerRow) => { players: number | null; maxPlayers: number | null } | null
  // Launcher only: an action at the end of every row (Join). Absent on the
  // site, where the row itself opens the server.
  rowAction?: (s: ServerRow) => ReactNode
  // Launcher only: a narrow status column right after the star (the play /
  // download glyph that is also the row's Play).
  rowStart?: (s: ServerRow) => ReactNode
  // Launcher only: replaces the star cell with its own control.
  starControl?: (s: ServerRow) => ReactNode
  // Launcher only: the server whose join sheet is open gets a highlighted row.
  selectedId?: number | null
  // Optional URL-driven preset. When set, the browser starts pre-filtered and
  // shows a small "Showing servers running X" header. `null` = normal homepage.
  urlFilter?: UrlFilter | null
  onClearUrlFilter?: () => void
  // A filter set to apply when presetKey changes (the launcher's Discover
  // lanes open the browser on their own set). Replaces the current filters.
  preset?: Partial<ServerFilters>
  presetKey?: number
}

const noTrack: TrackFn = () => {}

export const ServerBrowser = memo(function ServerBrowser({
  opts, onOpen, favs, onToggleFav, api, track, pingFor,
  pingHeader = 'Est. ping',
  pingTitle = "Estimated from the distance between you and the server. A browser cannot measure real game-server ping.",
  pingPrefix = '~',
  liveFor, rowAction, rowStart, starControl, selectedId,
  urlFilter, onClearUrlFilter, preset, presetKey,
}: ServerBrowserProps) {
  const trackEvent = track ?? noTrack
  const env = useMemo(() => ({ api, track: trackEvent }), [api, trackEvent])
  // Seed the search from ?q= so shareable / SEO search-box URLs land pre-filtered.
  // The URL filter (if present) overrides the search seed since they're meant for
  // a different intent (browse all servers running mod X vs. text search).
  const [f, setF] = useState<ServerFilters>(() => {
    // Default to the Community context (the ~99% case); Official is opt-in.
    if (urlFilter) return { scope: 'community', ...seedFromUrlFilter(urlFilter) }
    const q = new URLSearchParams(window.location.search).get('q')
    return q ? { ...defaultFilters, scope: 'community', q } : { ...defaultFilters, scope: 'community' }
  })
  // Apply a preset handed in from outside (Discover lanes); a new key means a
  // new request even when the filters happen to be the same.
  useEffect(() => {
    if (!presetKey) return
    setF({ ...defaultFilters, scope: 'community', ...(preset ?? {}) })
    setQInput((preset?.q as string | undefined) ?? '')
    setFavMode(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetKey])
  // Human-readable label + count for the context banner above the table. Filled
  // asynchronously via /api/mod-info or /api/country-info. The map case resolves
  // synchronously from the shared MAP_NAMES dict.
  const [urlContext, setUrlContext] = useState<{
    title: string; sub?: string; workshop?: string
  } | null>(() => urlFilter ? urlContextFromFilter(urlFilter) : null)
  // Whenever the URL filter changes (back/fwd, new deep link), re-seed and re-fetch.
  useEffect(() => {
    if (!urlFilter) {
      setUrlContext(null)
      // If the URL is being cleared explicitly, reset to defaults; otherwise
      // leave the user-typed filter alone (they may be on the homepage with
      // existing filters from interactive selection).
      return
    }
    setF(seedFromUrlFilter(urlFilter))
    setUrlContext(urlContextFromFilter(urlFilter))
    if (urlFilter.kind === 'mod') {
      api.modInfo(urlFilter.id).then((info) => {
        setUrlContext({
          title: `Servers running ${info.name}`,
          sub: `${info.server_count} live server${info.server_count === 1 ? '' : 's'} · ranked best first`,
          workshop: info.workshop_url,
        })
      }).catch(() => {})
    } else if (urlFilter.kind === 'country') {
      api.countryInfo(urlFilter.slug).then((info) => {
        setF((p) => ({ ...p, country: [info.code] }))
        setUrlContext({
          title: `Servers in ${info.name}`,
          sub: `${info.server_count} live server${info.server_count === 1 ? '' : 's'} · ranked best first`,
        })
      }).catch(() => {})
    }
    // Scroll the browser into view shortly after the layout settles. We give
    // the page a tick so the hero and trending strip have laid out first.
    const t = setTimeout(() => {
      document.getElementById('server-browser-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 150)
    return () => clearTimeout(t)
  }, [urlFilter])
  const [favMode, setFavMode] = useState(false)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState<number>(() => readPageSize())
  const [showFilters, setShowFilters] = useState(false)
  // Region radar / "📍 Near me" is paused (not ready yet). When it comes back it'll
  // own f.country via a `radar` state + setRegion callback; see git for the version.
  const [rows, setRows] = useState<ServerRow[]>([])
  const [total, setTotal] = useState(0)
  const [totalAll, setTotalAll] = useState(0)
  const [loading, setLoading] = useState(false)
  // Search text is debounced into the filters; toggles/sort apply instantly.
  const [qInput, setQInput] = useState(f.q ?? '')
  const [searchFocused, setSearchFocused] = useState(false)
  const [recents, setRecents] = useState<string[]>(() => readRecents())
  const searchWrapRef = useRef<HTMLDivElement>(null)
  const reqId = useRef(0)
  const favSet = useMemo(() => new Set(favs), [favs])

  useEffect(() => {
    const t = setTimeout(() => {
      const q = qInput.trim() || undefined
      setF((p) => {
        if (p.q === q) return p
        if (q) {
          trackEvent('search', '', q.slice(0, 80))
          // Only commit to recents once the debounce settles — avoids saving
          // every keystroke en-route to the real query.
          setRecents((r) => pushRecent(q, r))
        }
        return { ...p, q }
      })
    }, 180)
    return () => clearTimeout(t)
  }, [qInput])

  // Close the recent-searches dropdown on outside click / Escape.
  useEffect(() => {
    if (!searchFocused) return
    const click = (e: MouseEvent) => {
      if (!searchWrapRef.current?.contains(e.target as Node)) setSearchFocused(false)
    }
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') setSearchFocused(false) }
    document.addEventListener('mousedown', click)
    document.addEventListener('keydown', key)
    return () => {
      document.removeEventListener('mousedown', click)
      document.removeEventListener('keydown', key)
    }
  }, [searchFocused])

  // Filter the recents list against the current input so it acts as a
  // prefix-aware suggester. When empty, show everything.
  const recentMatches = (() => {
    const q = qInput.trim().toLowerCase()
    if (!q) return recents
    return recents.filter((r) => r.toLowerCase() !== q && r.toLowerCase().includes(q))
  })()

  // The favourites list is read through a ref so that starring a row does NOT
  // re-create `load` (and refetch the whole table) when favourites mode is off.
  // favsKey only changes while favMode is on, which is when the list matters.
  const favsRef = useRef(favs)
  favsRef.current = favs
  const favsKey = favMode ? favs.join(',') : ''
  const load = useCallback(() => {
    // Favourites mode fetches the saved ids directly (online or not).
    const favList = favsRef.current
    if (favMode && favList.length === 0) { setRows([]); setTotal(0); setTotalAll(0); return }
    const query: ServerFilters = favMode
      ? { ids: favList, sort: f.sort, dir: f.dir, limit: pageSize, offset: page * pageSize }
      : { ...f, limit: pageSize, offset: page * pageSize }
    const id = ++reqId.current
    setLoading(true)
    api.servers(query)
      .then((r) => { if (id === reqId.current) { setRows(r.servers); setTotal(r.total); setTotalAll(r.total_all) } })
      .catch(() => {})
      .finally(() => { if (id === reqId.current) setLoading(false) })
  }, [f, page, pageSize, favMode, favsKey]) // eslint-disable-line react-hooks/exhaustive-deps
  // Any filter/sort/mode/page-size change jumps back to the first page IN THE
  // SAME render pass (adjust-state-during-render), so the load effect below
  // fires once with page 0 instead of fetching the stale page first and the
  // reset page second.
  const filterKey = JSON.stringify([f, favMode, pageSize])
  const [prevFilterKey, setPrevFilterKey] = useState(filterKey)
  if (filterKey !== prevFilterKey) {
    setPrevFilterKey(filterKey)
    if (page !== 0) setPage(0)
  }
  // Fire immediately — filter toggles shouldn't feel laggy; the text box is already debounced above.
  useEffect(() => { load() }, [load])

  const pages = Math.max(1, Math.ceil(total / pageSize))
  const from = total === 0 ? 0 : page * pageSize + 1
  const to = Math.min(total, page * pageSize + rows.length)
  const [pageInput, setPageInput] = useState('1')
  useEffect(() => { setPageInput(String(page + 1)) }, [page])
  const gotoPage = () => {
    const n = parseInt(pageInput, 10)
    if (!isNaN(n)) setPage(Math.min(pages - 1, Math.max(0, n - 1)))
    else setPageInput(String(page + 1))
  }

  const setOne = (k: keyof ServerFilters, v: string | boolean | undefined) => setF((p) => ({ ...p, [k]: v }))
  // The Badlands pill is on when the map filter is exactly the Nasdara map.
  const badlands = (f.map?.length ?? 0) === 1 && f.map?.[0] === 'nasdara'
  const toggleIn = (k: 'country' | 'map' | 'mod', id: string) => setF((p) => {
    const cur = p[k] ?? []
    const adding = !cur.includes(id)
    trackEvent('filter_use', k, (adding ? '+' : '-') + id)
    return { ...p, [k]: adding ? [...cur, id] : cur.filter((x) => x !== id) }
  })
  // (perspective is now a segmented control in the toolbar; togglePp removed.)

  const hidden = Math.max(0, totalAll - total)
  // Facet counts must reflect the CURRENT context (Community/Official + playstyle
  // + online + population), so they update as you switch and never offer maps/
  // mods that can't exist in the current view. Re-fetch when that context
  // changes; fall back to the global `opts` prop until the first context fetch
  // lands. (Deliberately NOT keyed on map/country/mod selections — you pick
  // those within the context; the backend caches the small context key space.)
  const [facets, setFacets] = useState<FilterOptions | null>(null)
  useEffect(() => {
    let live = true
    api.filters({ scope: f.scope, mode: f.mode, online: f.online, min_players: f.min_players, max_players: f.max_players, noAi: f.noAi })
      .then((r) => { if (live) setFacets(r) }).catch(() => {})
    return () => { live = false }
  }, [f.scope, f.mode, f.online, f.min_players, f.max_players, f.noAi])
  const fo = facets ?? opts
  const euCount = (fo?.countries ?? []).filter((c) => EU_SET.has(c.code)).reduce((a, c) => a + c.count, 0)
  const countryOpts = [
    ...(euCount > 0 ? [{ id: 'EU', label: '🇪🇺 EU', count: euCount }] : []),
    ...(fo?.countries ?? []).map((c) => ({ id: c.code, label: `${flag(c.code)} ${c.code}`, count: c.count })),
  ]
  const mapOpts = (fo?.maps ?? []).map((m) => ({ id: m.map, label: mapLabel(m.map), count: m.count }))
  const modOpts = (fo?.mods ?? []).map((m) => ({ id: String(m.id), label: (m.name ?? `#${m.id}`).slice(0, 22), count: m.count }))
  const { customMods, addCustomMod, removeCustomMod } = useCustomMods()

  const activeCount = (f.country?.length ?? 0) + (f.map?.length ?? 0) + (f.mod?.length ?? 0) +
    (f.q ? 1 : 0) + (f.min_players ? 1 : 0) + (f.max_players ? 1 : 0) +
    (!!f.hide_empty !== !!defaultFilters.hide_empty ? 1 : 0) + (f.hide_full ? 1 : 0) + (!!f.open_only !== !!defaultFilters.open_only ? 1 : 0) + (f.pp?.length ?? 0) +
    (f.wiped ? 1 : 0) + (f.wipe ? 1 : 0) + (f.daynight ? 1 : 0) + (f.igFrom != null ? 1 : 0) + (f.igTo != null ? 1 : 0) +
    (f.mode ? 1 : 0) + (f.vsFrom != null ? 1 : 0) + (f.vsTo != null ? 1 : 0) +
    (f.noAi ? 1 : 0)

  const sortDescriptor = { column: f.sort ?? 'players', direction: (f.dir === 'asc' ? 'ascending' : 'descending') as 'ascending' | 'descending' }
  const onSortChange = (d: { column?: string | number; direction?: string }) =>
    setF((p) => ({ ...p, sort: String(d.column), dir: d.direction === 'ascending' ? 'asc' : 'desc' }))

  return (
    <BrowserEnv.Provider value={env}>
    <Card>
      <Card.Content className="space-y-5">
        {/* URL-driven context banner. Appears only when the visitor landed on
            /mod/X, /map/X or /servers/X — explains what's being filtered and
            offers a one-click way back to the unfiltered list. */}
        {urlFilter && urlContext && (
          <div className="-mt-1 rounded-xl border border-[var(--brand)]/40 bg-[var(--brand)]/[0.06]
                          px-3.5 py-2.5 flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-[13px] text-[var(--foreground)] leading-snug">{urlContext.title}</div>
              {urlContext.sub && (
                <div className="mono text-[10px] uppercase tracking-wider text-[var(--muted)] mt-0.5">
                  {urlContext.sub}
                </div>
              )}
              {urlContext.workshop && (
                <a href={urlContext.workshop} target="_blank" rel="noopener noreferrer"
                  className="mono text-[10px] uppercase tracking-wider text-[var(--brand)] hover:opacity-90 mt-1 inline-block">
                  View on Steam Workshop →
                </a>
              )}
            </div>
            <button type="button" onClick={() => { setF({ ...defaultFilters, scope: 'community' }); onClearUrlFilter?.() }}
              className="mono text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-md
                         border border-[var(--border)] text-[var(--muted)] hover:text-[var(--foreground)]
                         shrink-0">
              Clear
            </button>
          </div>
        )}
        {/* search */}
        <div ref={searchWrapRef} className="relative">
          <TextField aria-label="Search name" value={qInput} onChange={setQInput}>
            <Label className="mono text-[11px] uppercase tracking-wider text-[var(--muted)] mb-1.5 block">Search by name</Label>
            {/* The Input is wrapped in a relative div so the Search icon can be
                overlaid as an absolute child without breaking the TextField/Input
                ARIA wiring. paddingLeft on the input is generous enough that the
                placeholder never crashes into the icon. The `search-flash` class
                adds the left-to-right brand-coloured sweep on focus (defined in
                index.css). */}
            <div className="relative search-flash">
              <Search size={16} aria-hidden
                className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--muted)] pointer-events-none z-10" />
              <Input
                className="field-input"
                style={{ minHeight: 60, paddingLeft: 52, paddingRight: 18, fontSize: 16 }}
                placeholder="e.g. vanilla, namalsk, [EU], deer isle…"
                onFocus={() => setSearchFocused(true)}
                autoComplete="off"
              />
            </div>
          </TextField>
          {/* Recent-searches dropdown. Opens on focus + when there are entries
              to suggest. The actual list is `recentMatches` (filtered against
              the current input). Clicking a chip applies it immediately so the
              user doesn't have to wait for the debounce. */}
          {searchFocused && recentMatches.length > 0 && (
            <div role="listbox" aria-label="Recent searches"
              className="absolute left-0 right-0 top-full mt-1.5 z-20 rounded-xl bg-[var(--background-secondary)] p-2"
              style={{ boxShadow: '0 18px 40px rgba(0,0,0,0.55)' }}>
              <div className="flex items-center justify-between px-2 pt-1 pb-2">
                <span className="mono text-[10px] uppercase tracking-wider text-[var(--muted)]">Recent searches</span>
                <button type="button"
                  onMouseDown={(e) => { e.preventDefault(); writeRecents([]); setRecents([]); setSearchFocused(false) }}
                  className="mono text-[10px] uppercase tracking-wider text-[var(--muted)] hover:text-[var(--foreground)]">
                  Clear all
                </button>
              </div>
              <ul className="flex flex-col">
                {recentMatches.map((r) => (
                  <li key={r}>
                    <div className="group flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-[var(--background-tertiary)]">
                      <button type="button" role="option"
                        onMouseDown={(e) => {
                          // Apply on mousedown so blur doesn't fire first and close us
                          e.preventDefault()
                          setQInput(r)
                          trackEvent('search', 'recent', r.slice(0, 80))
                          setF((p) => ({ ...p, q: r }))
                          setSearchFocused(false)
                        }}
                        className="flex-1 text-left text-sm text-[var(--foreground)] inline-flex items-center gap-2 min-w-0">
                        <ClockIcon size={16} className="text-[var(--muted)] shrink-0" />
                        <span className="truncate">{r}</span>
                      </button>
                      <button type="button" aria-label={`Remove "${r}"`}
                        onMouseDown={(e) => {
                          e.preventDefault()
                          setRecents((cur) => { const next = cur.filter((x) => x !== r); writeRecents(next); return next })
                        }}
                        /* visible on touch (no hover): show always on phone/tablet,
                           fade-in on hover for desktop. */
                        className="mono text-[12px] text-[var(--muted)] hover:text-[var(--foreground)] opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity px-2 py-1">
                        ✕
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Primary context switch — Community vs Official. Official servers are
            Bohemia's public-hive, always-vanilla, no-mods, no-playstyle-variant
            set, so picking it hides the mode/mod filters that don't apply. */}
        {(() => {
          const official = f.scope === 'official'
          return (
            <div className="inline-flex items-center gap-2">
              <span className="mono text-[10px] uppercase tracking-wider text-[var(--muted)]">Servers</span>
              <Segmented<'community' | 'official'> ariaLabel="Community or official servers"
                options={[{ v: 'community', label: 'Community' }, { v: 'official', label: 'Official' }]}
                value={official ? 'official' : 'community'}
                onChange={(v) => {
                  trackEvent('filter_use', 'scope', v)
                  // Switching to Official clears the filters that don't apply to
                  // it (playstyle, mods, wipes) so they can't silently zero the list.
                  setF((p) => v === 'official'
                    ? { ...p, scope: 'official', mode: undefined, mod: undefined, vsFrom: undefined, vsTo: undefined, wiped: undefined, wipe: undefined, noAi: undefined }
                    : { ...p, scope: 'community' })
                }} />
            </div>
          )
        })()}

        {/* Row 1 — mode / perspective / sun & moon / online (the "essentials") */}
        {(() => {
          const official = f.scope === 'official'
          const ppValue: 'any' | '1pp' | '3pp' =
            (f.pp ?? []).includes('1pp') ? '1pp' :
            (f.pp ?? []).includes('3pp') ? '3pp' : 'any'
          // Each label+control pair is wrapped in an inline-flex group so a
          // wrap never orphans a label from its controls — important on phone
          // widths where the row has to break into 2-3 lines.
          return (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              {!official && (
              <div className="inline-flex items-center gap-2">
                <span className="mono text-[10px] uppercase tracking-wider text-[var(--muted)]">Mode</span>
                <Segmented<'any' | 'pvp' | 'pve' | 'rp'> ariaLabel="Server playstyle"
                  options={[
                    { v: 'any', label: 'Any' },
                    { v: 'pvp', label: 'PVP' },
                    { v: 'pve', label: 'PVE' },
                    { v: 'rp', label: 'RP' },
                  ]}
                  value={f.mode ?? 'any'}
                  onChange={(v) => { trackEvent('filter_use', 'mode', v); setF((p) => ({ ...p, mode: v === 'any' ? undefined : v })) }} />
              </div>
              )}
              {/* AI bot mods. Graded rather than a plain on/off because the mods
                  differ in how much they change the game: "Limited" still allows
                  AI tied to events or zones (paradrops, convoys, warzones) while
                  excluding AI that roams and hunts you, which is what people
                  actually complain about. Community-only — official servers are
                  mod-free by definition. */}
              {!official && (
              <div className="inline-flex items-center gap-2">
                {/* The stop names alone don't say what comes off the list, and
                    guessing wrong here is expensive: "None" drops ~3k servers.
                    Spell out each stop rather than making players test it. */}
                <span className="mono text-[10px] uppercase tracking-wider text-[var(--muted)] inline-flex items-center gap-1">
                  AI
                  <Tip tappable width={288} content={
                    <span>
                      Hides servers running mods that add AI bots, which owners
                      often don't advertise.
                      <br /><br />
                      <b className="text-[var(--foreground)]">Limited</b> — hides AI that roams
                      the map and hunts you (Expansion AI, AI Bandits and similar).
                      <br /><br />
                      <b className="text-[var(--foreground)]">None</b> — hides those, plus AI
                      tied to events or places (paradrops, convoys, war zones) and
                      NPCs that don't fight, like traders.
                    </span>
                  }><span className="inline-flex"><InfoGlyph /></span></Tip>
                </span>
                <Segmented<'any' | 'limited' | 'none'> ariaLabel="AI bot mods"
                  options={[
                    { v: 'any', label: 'Any' },
                    { v: 'limited', label: 'Limited' },
                    { v: 'none', label: 'None' },
                  ]}
                  value={f.noAi === 3 ? 'none' : f.noAi ? 'limited' : 'any'}
                  onChange={(v) => {
                    trackEvent('filter_use', 'no_ai', v)
                    setF((p) => ({ ...p, noAi: v === 'any' ? undefined : v === 'limited' ? 1 : 3 }))
                  }} />
              </div>
              )}
              <div className="inline-flex items-center gap-2">
                <span className="mono text-[10px] uppercase tracking-wider text-[var(--muted)]">Perspective</span>
                <Segmented<'any' | '1pp' | '3pp'> ariaLabel="Perspective"
                  options={[{ v: 'any', label: 'Any' }, { v: '1pp', label: '1PP' }, { v: '3pp', label: '3PP' }]}
                  value={ppValue}
                  onChange={(v) => { trackEvent('filter_use', 'pp', v); setF((p) => ({ ...p, pp: v === 'any' ? undefined : [v] })) }} />
              </div>
              <div className="inline-flex items-center gap-2">
                <span className="mono text-[10px] uppercase tracking-wider text-[var(--muted)]">In-game</span>
                <IconToggle icon={<Sun size={16} />} label="Daytime servers" active={f.daynight === 'day'}
                  onClick={() => { trackEvent('filter_use', 'daynight', f.daynight === 'day' ? 'off' : 'day'); setF((p) => ({ ...p, daynight: p.daynight === 'day' ? undefined : 'day' })) }} />
                <IconToggle icon={<Moon size={16} />} label="Night servers" active={f.daynight === 'night'}
                  onClick={() => { trackEvent('filter_use', 'daynight', f.daynight === 'night' ? 'off' : 'night'); setF((p) => ({ ...p, daynight: p.daynight === 'night' ? undefined : 'night' })) }} />
              </div>
              <FilterPill label="Online now" active={f.online !== false} onChange={(v) => setOne('online', v)} />
            </div>
          )
        })()}

        {/* Row 2 — rule pills + Players / In-game range popovers + Clear */}
        {(() => {
          const playersLo = parseInt(f.min_players ?? '') || 0
          const playersHi = parseInt(f.max_players ?? '') || 128
          const setPlayersRange = (lo: number, hi: number) => {
            const clear = lo === 0 && hi === 128
            trackEvent('filter_use', 'players', clear ? 'reset' : `${lo}-${hi}`)
            setF((p) => ({ ...p, min_players: clear ? undefined : String(lo), max_players: clear ? undefined : String(hi) }))
          }
          const timeLo = f.igFrom ?? 0
          const timeHi = f.igTo ?? 23
          const setTimeRange = (lo: number, hi: number) => {
            const clear = lo === 0 && hi === 23
            trackEvent('filter_use', 'igtime', clear ? 'reset' : `${lo}-${hi}`)
            setF((p) => ({ ...p, igFrom: clear ? undefined : lo, igTo: clear ? undefined : hi }))
          }
          const vsLo = f.vsFrom ?? 0
          const vsHi = f.vsTo ?? 100
          const setVsRange = (lo: number, hi: number) => {
            const clear = lo === 0 && hi === 100
            trackEvent('filter_use', 'vanilla_scale', clear ? 'reset' : `${lo}-${hi}`)
            setF((p) => ({ ...p, vsFrom: clear ? undefined : lo, vsTo: clear ? undefined : hi }))
          }
          const official = f.scope === 'official'
          return (
            <div className="flex flex-wrap items-center gap-2">
              <FilterPill icon={<StarFilled size={16} />} label={`Favourites${favs.length ? ` (${favs.length})` : ''}`} active={favMode} onChange={setFavMode} />
              <FilterPill label="No password" active={!!f.open_only} onChange={(v) => setOne('open_only', v)} />
              <FilterPill label="Hide empty" active={!!f.hide_empty} onChange={(v) => setOne('hide_empty', v)} />
              <FilterPill label="Hide full" active={!!f.hide_full} onChange={(v) => setOne('hide_full', v)} />
              {/* Wiped and the Vanilla Scale range are community-only: official
                  servers are all vanilla on the public hive and don't advertise
                  wipes, so these are hidden under the Official tab. */}
              {!official && <FilterPill label="Wiped" active={!!f.wiped} onChange={(v) => setOne('wiped', v)} />}
              {/* The end of a wipe cycle (wipe engine): a wipe announced in the
                  name or predicted from past wipes, within the next week. */}
              {!official && features.wipes && <FilterPill label="Wipe soon" active={f.wipe === 'soon'} onChange={(v) => setOne('wipe', v ? 'soon' : undefined)} />}
              {!official && features.wipes && <FilterPill label="Wipe weekend" active={f.wipe === 'weekend'} onChange={(v) => setOne('wipe', v ? 'weekend' : undefined)} />}
              {/* Badlands (the Nasdara map, DLC of October 2026): its own pill
                  so it is one tap the day the servers appear, in both scopes
                  because Bohemia runs officials on it as well. */}
              <FilterPill label="Badlands" tag="New" active={badlands} onChange={(v) => {
                trackEvent('filter_use', 'badlands', v ? 'on' : 'off')
                setF((p) => ({ ...p, map: v ? ['nasdara'] : undefined }))
              }} />
              <RangePopover
                pillIcon={<Users size={16} />} pillLabel="Players" unit="players"
                panelTitle="Players online" panelIcon={<Users size={16} />}
                min={0} max={128} lo={playersLo} hi={playersHi} onChange={setPlayersRange}
                tickLabels={['0', '32', '64', '96', '128']}
                presets={[
                  { label: 'Empty (0–10)', lo: 0, hi: 10 },
                  { label: 'Light (10–60)', lo: 10, hi: 60 },
                  { label: 'Busy (30–100)', lo: 30, hi: 100 },
                  { label: 'Full (90+)', lo: 90, hi: 128 },
                ]} />
              <RangePopover
                pillIcon={<ClockIcon size={16} />} pillLabel="In-game" unit="in-game"
                panelTitle="In-game time" panelIcon={<ClockIcon size={16} />}
                min={0} max={23} lo={timeLo} hi={timeHi} onChange={setTimeRange}
                format={(v) => String(v).padStart(2, '0') + ':00'}
                tickGlyphs={[
                  <><Moon size={14} />00</>,
                  <>06</>,
                  <><Sun size={14} />12</>,
                  <>18</>,
                  <><Moon size={14} />23</>,
                ]}
                presets={[
                  { label: 'Dawn (4–7)', lo: 4, hi: 7 },
                  { label: 'Morning (6–12)', lo: 6, hi: 12 },
                  { label: 'Afternoon (12–18)', lo: 12, hi: 18 },
                  { label: 'Evening (17–22)', lo: 17, hi: 22 },
                ]} />
              {!official && (
              <RangePopover
                pillIcon={<Gauge size={16} />} pillLabel="Vanilla Scale" unit="scale"
                panelTitle="Vanilla → Extreme Mod" panelIcon={<Gauge size={16} />}
                min={0} max={100} lo={vsLo} hi={vsHi} onChange={setVsRange}
                tickLabels={['Vanilla', '25', '50', '75', 'Extreme']}
                presets={[
                  { label: 'Vanilla only (0)', lo: 0, hi: 0 },
                  { label: 'Vanilla+ (0–20)', lo: 0, hi: 20 },
                  { label: 'Any mods (1–100)', lo: 1, hi: 100 },
                  { label: 'Modded (45–70)', lo: 45, hi: 70 },
                  { label: 'Heavy (70–100)', lo: 70, hi: 100 },
                ]}
                experimental
                note={<>Each mod is scored by how much it changes gameplay, then combined into a 0–100 mod level. It reflects installed mods, not loot or economy tweaks. Still tuning, so expect the odd surprise.</>} />
              )}
              {/* Clear sits in-flow right after the pills it resets (not exiled to
                  the far edge) and is styled as an ACTION, not a filter state:
                  transparent with brand text + ×, distinct from both idle and
                  active pills, filling in on hover. */}
              {activeCount > 0 && (
                <button type="button" onClick={() => { trackEvent('filter_use', 'clear_all', String(activeCount)); setF({ ...defaultFilters, scope: f.scope }); setFavMode(false) }}
                  style={{ minHeight: 40 }}
                  className="mono text-xs uppercase tracking-wider rounded-full px-4 inline-flex items-center gap-1.5 text-[var(--brand)] bg-transparent hover:bg-[var(--brand-soft)] transition-colors font-semibold">
                  <X size={16} /> Clear all ({activeCount})
                </button>
              )}
            </div>
          )
        })()}

        {/* advanced: map / country / mods / player range, tucked behind one toggle */}
        {(() => {
          // Player count + In-game time live in their own popovers now, so they
          // don't count toward the "More filters (N)" badge.
          const facetCount = (f.map?.length ?? 0) + (f.country?.length ?? 0) + (f.mod?.length ?? 0)
          const sel: { group: 'map' | 'country' | 'mod'; id: string; label: string }[] = [
            ...(f.map ?? []).map((id) => ({ group: 'map' as const, id, label: id })),
            ...(f.country ?? []).map((id) => ({ group: 'country' as const, id, label: `${flag(id)} ${id}` })),
            ...(f.mod ?? []).map((id) => ({
              group: 'mod' as const, id,
              label: opts?.mods.find((m) => String(m.id) === id)?.name
                ?? customMods.find((m) => m.id === id)?.name ?? `#${id}`,
            })),
          ]
          return (
            <div className="space-y-3">
              <div className="flex items-center flex-wrap gap-2">
                <button type="button" onClick={() => setShowFilters((v) => !v)}
                  style={{ minHeight: 40 }}
                  className={`mono text-xs uppercase tracking-wider rounded-full px-4 transition-colors inline-flex items-center gap-2 ${showFilters || facetCount
                    ? 'bg-[var(--brand-soft)] text-[var(--brand)] font-semibold ring-1 ring-[var(--brand)] [--icon-accent:var(--brand)]'
                    : 'bg-[var(--background-tertiary)] text-[var(--muted)] hover:text-[var(--foreground)]'}`}>
                  <SlidersHorizontal size={16} /> More filters{facetCount ? ` (${facetCount})` : ''}
                  {showFilters ? <ChevronDown size={16} noFloor /> : <ChevronRight size={16} noFloor />}
                </button>
                {sel.map((c) => (
                  <span key={c.group + c.id} className={`px-2.5 py-1 inline-flex items-center gap-1.5 ${chipClass(true)}`}>
                    {c.label}
                    <button type="button" onClick={() => toggleIn(c.group, c.id)} className="opacity-70 hover:opacity-100"><X size={18} /></button>
                  </span>
                ))}
              </div>
              {showFilters && (
                <div className="space-y-4 rounded-xl bg-[var(--background-tertiary)]/50 p-3.5">
                  <ChipToggleGroup label="Map" options={mapOpts} selected={f.map ?? []} onToggle={(id) => toggleIn('map', id)} />
                  <ChipToggleGroup label="Country" options={countryOpts} selected={f.country ?? []} onToggle={(id) => toggleIn('country', id)} />
                  {/* Mods facet is community-only — official servers never run mods. */}
                  {f.scope !== 'official' && (
                  <div className="space-y-3">
                    <ChipToggleGroup label="Mods" options={modOpts} selected={f.mod ?? []} onToggle={(id) => toggleIn('mod', id)} max={14} />
                    {customMods.length > 0 && (
                      <div>
                        <div className="mono text-[11px] uppercase tracking-wider text-[var(--muted)] mb-2">Pinned</div>
                        <div className="flex flex-wrap gap-1.5">
                          {customMods.map((m) => {
                            const on = (f.mod ?? []).includes(m.id)
                            return (
                              <span key={m.id} className={`inline-flex items-center ${chipClass(on)}`}>
                                <button type="button" onClick={() => toggleIn('mod', m.id)} aria-pressed={on}
                                  className="pl-2.5 py-1 rounded-l-full">
                                  {m.name.slice(0, 28)}
                                </button>
                                <button type="button" aria-label={`Remove ${m.name} chip`}
                                  onClick={() => { removeCustomMod(m.id); if (on) toggleIn('mod', m.id) }}
                                  className="px-2 py-1 rounded-r-full opacity-70 hover:opacity-100">
                                  ×
                                </button>
                              </span>
                            )
                          })}
                        </div>
                      </div>
                    )}
                    <ModSearch
                      existing={new Set([...modOpts.map((o) => o.id), ...customMods.map((m) => m.id)])}
                      onPick={(m) => { addCustomMod(m); if (!(f.mod ?? []).includes(m.id)) toggleIn('mod', m.id) }} />
                  </div>
                  )}
                  {/* Player count + In-game time live in their own popovers in the
                      toolbar above. Map / Country / Mods stay here because they are
                      30-80 chips each and would dominate the panel otherwise. */}
                </div>
              )}
            </div>
          )
        })()}

        {/* count + hidden hint */}
        <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mono text-xs text-[var(--muted)]">
          <span><span className="text-[var(--foreground)]">{fmt(from)}–{fmt(to)}</span> of <span className="text-[var(--foreground)]">{fmt(total)}</span> servers · click a column to sort · <RankInfo label="how we rank" /></span>
          {loading && (
            <span className="inline-flex items-center gap-1.5" style={{ color: '#FF3C15' }}>
              <span className="inline-block h-3 w-3 rounded-full animate-spin" style={{ border: '2px solid color-mix(in oklab, #FF3C15 30%, transparent)', borderTopColor: '#FF3C15' }} />
              updating…
            </span>
          )}
          {hidden > 0 && activeCount > 0 && f.online !== false && (
            <button type="button" className="text-[var(--accent)] underline decoration-dotted hover:opacity-80"
              onClick={() => setOne('online', false)}>
              +{fmt(hidden)} offline, include them
            </button>
          )}
        </div>

        <ServerTable rows={rows} favSet={favSet} onToggleFav={onToggleFav} onOpen={(id) => onOpen(id, f.mod)}
          pingFor={pingFor} pingHeader={pingHeader} pingTitle={pingTitle} pingPrefix={pingPrefix} liveFor={liveFor}
          rowAction={rowAction} rowStart={rowStart} starControl={starControl} selectedId={selectedId} sortDescriptor={sortDescriptor} onSortChange={onSortChange}
          emptyState={(
            <div className="text-[var(--muted)] py-6 text-center">
              {favMode ? 'No favourites yet. Tap the ☆ on any server to save it here.' : badlands ? (
                /* Nothing on the Nasdara map yet: say what is coming instead of
                   an empty table. The block goes the day the first servers show. */
                <div className="mx-auto max-w-[520px] px-6 py-10 text-center">
                  <div className="mono text-[11px] uppercase tracking-wider text-[var(--brand)]">Coming October 15, 2026</div>
                  <div className="text-[22px] font-semibold tracking-tight text-[var(--foreground)] mt-2">Badlands is not out yet</div>
                  <p className="text-[14px] leading-relaxed mt-3">The Nasdara map, 267 km² of desert west of Takistan, arrives with the Badlands DLC. Servers on it appear in this list the moment they go live; this filter is already wired to them.</p>
                  <p className="mono text-[11px] uppercase tracking-wider mt-5">Until then: <button type="button" className="text-[var(--foreground)] underline decoration-dotted underline-offset-4" onClick={() => setF((p) => ({ ...p, map: undefined }))}>all maps</button></p>
                </div>
              ) : <>
                No servers match these filters.
                {hidden > 0 && f.online !== false && <> <button type="button" className="text-[var(--accent)] underline decoration-dotted" onClick={() => setOne('online', false)}>Include {fmt(hidden)} offline</button>.</>}
              </>}
            </div>
          )} />

        {/* pagination + page size — the size selector lives next to the pager
            so the "show more per page" affordance is right where you'd expect
            after running out of room. Stays visible even on a single page so
            the user can still bump the size to consolidate. */}
        <div className="flex items-center justify-center flex-wrap gap-x-1.5 gap-y-2 pt-1">
          {pages > 1 && (
            <div className="inline-flex items-center gap-1.5">
              {/* Skip-to-first/last are hidden on phones — Prev/Next + jump-to-page
                  are enough at that width, and 4 buttons stop crowding into a
                  second row. */}
              <Button size="sm" variant="secondary" isDisabled={page <= 0} onPress={() => setPage(0)} aria-label="First page" className="hidden sm:inline-flex"><ChevronsLeft size={18} /></Button>
              <Button size="sm" variant="secondary" isDisabled={page <= 0} onPress={() => setPage((p) => Math.max(0, p - 1))} aria-label="Previous page"><ChevronLeft size={16} /><span className="hidden sm:inline ml-1">Prev</span></Button>
              <span className="mono text-xs text-[var(--muted)] inline-flex items-center gap-1.5 px-1 whitespace-nowrap">
                <span className="hidden sm:inline">Page</span>
                <input type="number" min={1} max={pages} value={pageInput}
                  onChange={(e) => setPageInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') gotoPage() }}
                  onBlur={gotoPage} aria-label="Page number"
                  className="field-input w-14 px-1 text-center mono text-xs" style={{ minHeight: 32 }} />
                / {fmt(pages)}
              </span>
              <Button size="sm" variant="secondary" isDisabled={page >= pages - 1} onPress={() => setPage((p) => Math.min(pages - 1, p + 1))} aria-label="Next page"><span className="hidden sm:inline mr-1">Next</span><ChevronRight size={16} /></Button>
              <Button size="sm" variant="secondary" isDisabled={page >= pages - 1} onPress={() => setPage(pages - 1)} aria-label="Last page" className="hidden sm:inline-flex"><ChevronsRight size={18} /></Button>
            </div>
          )}
          <label className="mono text-xs text-[var(--muted)] inline-flex items-center gap-1.5 sm:ml-2 whitespace-nowrap">
            Show
            <select value={pageSize}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10)
                if (PAGE_SIZE_OPTIONS.includes(n as typeof PAGE_SIZE_OPTIONS[number])) {
                  setPageSize(n)
                  try { localStorage.setItem(PAGE_SIZE_KEY, String(n)) } catch { /* quota */ }
                  trackEvent('filter_use', 'page_size', String(n))
                }
              }}
              className="field-input mono text-xs px-2"
              style={{ minHeight: 32, width: 'auto', paddingRight: 24 }}>
              {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <span className="hidden sm:inline">per page</span>
          </label>
        </div>
      </Card.Content>
    </Card>
    </BrowserEnv.Provider>
  )
})
